import { useState } from 'react'
import { toast } from 'sonner'

import { EInvoiceManualIssueDialog } from './einvoice-manual-issue-dialog'
import { EInvoiceSkipDialog } from './einvoice-skip-dialog'
import { OpenManualIssueProvider } from './einvoice-manual-switch'
import type { ReactNode } from 'react'
import type {
    EInvoice,
    EInvoiceResolutionSource,
    EInvoiceStatus,
    ManualCandidate,
} from '@/lib/api/einvoice-api'
import { Button } from '@/components/ui/button'
import { getEInvoiceFailureMessage } from '@/lib/einvoice-failure-message'
import { misaErrorCode, refusalSentence } from '@/lib/einvoice-stuck-copy'
import { pendingAgeDays } from '@/lib/einvoice-pending-age'
import { m } from '@/lib/paraglide/messages'

/** Statuses a person may close by hand; the server enforces the same list. */
const CLOSABLE: readonly EInvoiceStatus[] = [
    'draft',
    'pushed',
    'failed',
    'publish_failed',
    'rejected',
    'publish_duplicated',
]

/** MISA gave no answer to a publication for this long: staff cannot act yet. */
const STUCK_AFTER_MS = 30 * 60 * 1000

export const isStuckAtMisa = (invoice: {
    status: EInvoiceStatus
    updated_at: string
    publication_requested_at?: string
}) =>
    invoice.status === 'publish_unknown' &&
    Date.now() -
        Date.parse(invoice.publication_requested_at ?? invoice.updated_at) >
        STUCK_AFTER_MS

export const isClosable = (status?: EInvoiceStatus) =>
    status === undefined || CLOSABLE.includes(status)

interface Props {
    orderId: string
    invoice?: EInvoice | null
    deliveredAt?: string
    source: EInvoiceResolutionSource
    /** The publish action of the existing flow; absent where none is allowed. */
    primary?: ReactNode
    /** Replaces the default sentence when the state has its own. */
    sentence?: string
    /** "Đã xuất trên MISA" leads, then the action in `primary`, then "Không xuất":
     *  the person must first rule out that the invoice was issued by hand. */
    closingFirst?: boolean
    /** No closing buttons at all: the state offers nothing but its own action. */
    hideClosing?: boolean
    /** Small text links under the buttons. */
    links?: ReactNode
    /** Asks MISA about this order now; resolves to the refreshed invoice. */
    refreshNow?: () => Promise<EInvoice | undefined>
}

/** The one sentence: how long the order has waited, plus what the old status
 *  box used to say about this invoice. */
function sentenceFor(
    invoice: EInvoice | null | undefined,
    waiting: string,
): string {
    switch (invoice?.status) {
        case 'pushed':
            return [
                waiting,
                m.einvoice_pending_old_draft(),
                invoice.stale ? m.einvoice_pending_stale() : '',
            ]
                .filter(Boolean)
                .join(' ')
        case 'publish_duplicated':
            return m.einvoice_pending_duplicated()
        case 'failed':
            return [waiting, getEInvoiceFailureMessage(invoice)]
                .filter(Boolean)
                .join(' ')
        case 'publish_failed':
            // A MISA refusal with a code reads from the one table of sentences.
            return misaErrorCode(invoice.error_message)
                ? refusalSentence(invoice.error_message)
                : [waiting, invoice.error_message].filter(Boolean).join(' ')
        default:
            return waiting
    }
}

/** One sentence about what is wrong plus the two ways to close the order by
 *  hand. Publishing stays with the existing buttons. */
export function EInvoicePendingActions({
    orderId,
    invoice,
    deliveredAt,
    source,
    primary,
    sentence,
    hideClosing = false,
    closingFirst = false,
    links,
    refreshNow,
}: Props) {
    const [dialog, setDialog] = useState<'manual' | 'skip'>()
    // A row chosen elsewhere (the republish confirmation) opens the dialog filled.
    const [chosen, setChosen] = useState<ManualCandidate>()
    const status = invoice?.status
    const hasOldDraft = status === 'pushed'
    const days = deliveredAt ? pendingAgeDays(deliveredAt) : 0
    const waiting = days
        ? m.einvoice_pending_sentence({ days: String(days) })
        : m.einvoice_pending_sentence_today()
    const close = () => {
        setDialog(undefined)
        setChosen(undefined)
    }
    const openWith = (candidate: ManualCandidate) => {
        setChosen(candidate)
        setDialog('manual')
    }
    // Staff asked to remove an old MISA draft that somebody had already signed:
    // pull the signed invoice in (read-only) and say what happened.
    const recoverSignedDraft = refreshNow
        ? () => {
              const manual = () =>
                  toast.warning(m.einvoice_already_issued_manual())
              void refreshNow()
                  .then((fresh) =>
                      fresh?.status === 'issued'
                          ? toast.success(m.einvoice_already_issued_pulled())
                          : manual(),
                  )
                  .catch(manual)
          }
        : undefined
    return (
        <OpenManualIssueProvider value={openWith}>
            <div className="space-y-2 rounded-md border p-3">
                <p className="text-sm">
                    {sentence ?? sentenceFor(invoice, waiting)}
                </p>
                <div className="flex flex-wrap gap-2">
                    {closingFirst ? null : primary}
                    {hideClosing ? null : (
                        <Button
                            type="button"
                            size="sm"
                            variant={closingFirst ? 'default' : 'outline'}
                            onClick={() => setDialog('manual')}
                        >
                            {m.einvoice_pending_issued_btn()}
                        </Button>
                    )}
                    {closingFirst ? primary : null}
                    {/* MISA says an invoice exists (or issued one the tax authority
                    refused): it must be linked, never dropped. */}
                    {status === 'publish_duplicated' ||
                    status === 'rejected' ||
                    hideClosing ? null : (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => setDialog('skip')}
                        >
                            {m.einvoice_pending_skip_btn()}
                        </Button>
                    )}
                </div>
                {links}
                {dialog === 'manual' ? (
                    <EInvoiceManualIssueDialog
                        orderId={orderId}
                        deliveredAt={deliveredAt}
                        hasOldDraft={hasOldDraft}
                        source={source}
                        initialCandidate={chosen}
                        onClose={close}
                        onAlreadyIssued={recoverSignedDraft}
                    />
                ) : null}
                {dialog === 'skip' ? (
                    <EInvoiceSkipDialog
                        orderId={orderId}
                        hasOldDraft={hasOldDraft}
                        source={source}
                        onClose={close}
                        onAlreadyIssued={recoverSignedDraft}
                    />
                ) : null}
            </div>
        </OpenManualIssueProvider>
    )
}
